import type { RemoveOrderShipmentInput } from '@repo/api-contracts';
import { useRef, useState } from 'react';
import { orderErrorCode, orderErrorMessage } from '@/shared/lib';
import { Button } from '@/shared/ui';

interface OrderShipmentActionsProps {
  readonly shipmentId: string;
  readonly canRemove: boolean;
  readonly canRetry: boolean;
  readonly onRemove: (input: RemoveOrderShipmentInput) => Promise<unknown>;
  readonly onRetry: (input: RemoveOrderShipmentInput) => Promise<unknown>;
  readonly onReload: () => void;
  readonly pending?: boolean;
}

/** 송장 한 건의 처리 — 송장 삭제(마지막·배송 완료 송장은 불가)와 다시 추적(등록 실패·추적 종료). 충돌이면 최신 상세를 다시 읽는다. */
export function OrderShipmentActions({
  shipmentId,
  canRemove,
  canRetry,
  onRemove,
  onRetry,
  onReload,
  pending,
}: OrderShipmentActionsProps) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const lock = useRef(false);
  async function perform(callback: OrderShipmentActionsProps['onRemove']) {
    if (lock.current || pending) return;
    lock.current = true;
    setSaving(true);
    setMessage(null);
    try {
      // 성공 뒤 상세는 쓰기 훅이 무효화해 다시 읽는다 — 여기서 또 읽으면 개인정보 접속기록이 겹친다.
      await callback({ shipmentId });
    } catch (error) {
      setMessage(orderErrorMessage(error));
      if (orderErrorCode(error) === 'ORDER_STATE_CONFLICT') onReload();
    } finally {
      setSaving(false);
      lock.current = false;
    }
  }
  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-foreground/[0.06] pt-3">
      <div className="flex flex-wrap items-center gap-2">
        {canRetry && (
          <Button
            variant="outline"
            size="sm"
            disabled={saving || pending}
            onClick={() => perform(onRetry)}
          >
            다시 추적
          </Button>
        )}
        {canRemove ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            disabled={saving || pending}
            onClick={() => perform(onRemove)}
          >
            송장 삭제
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">
            마지막 송장 또는 배송 완료 송장은 삭제할 수 없습니다.
          </p>
        )}
      </div>
      {message && (
        <p role="alert" className="text-sm text-destructive">
          {message}
        </p>
      )}
    </div>
  );
}
